import { useMemo } from 'react';
import { PlannerFilter } from '../types/planning';
import { ClientForms } from 'src/types/client';
import { ProviderForms } from 'src/types/provider';
import { filterPlanners } from 'src/helpers/planner-filter-helper/planners-filter-helper';

interface FilteredDataResult {
  filteredClients: ClientForms[];
  filteredProviders: ProviderForms[];
  filteredIds: string[];
  hasValidData: boolean;
}

interface UseFilteredDataProps {
  clientData: ClientForms[];
  providerData: ProviderForms[];
  filterValues: PlannerFilter;
  searchValue: string;
  isLoading: boolean;
  isDataUpdate?: boolean;
  utilizationData?: {
    Clients: ClientForms[];
    Provider: ProviderForms[];
  };
}

export const useFilteredData = ({
  clientData,
  providerData,
  filterValues,
  searchValue,
  isLoading,
  isDataUpdate = false,
  utilizationData
}: UseFilteredDataProps): FilteredDataResult => {
  // Helper to check if data has availability details
  const hasAvailabilityDetails = (item: any) => 
    Array.isArray(item.availabilityDetails) && 
    item.availabilityDetails.length > 0;

  // Filter clients with availability data
  const validClients = useMemo(() => {
    const sourceData = isDataUpdate 
      ? utilizationData?.Clients 
      : clientData;
    
    // Turn off this exclude logic to include all clients as base - by Hui 2025-05-6
    // return sourceData?.filter(hasAvailabilityDetails) || [];
    return sourceData || [];
  }, [clientData, utilizationData, isDataUpdate]);

  // Filter providers with availability data
  const validProviders = useMemo(() => {
    const sourceData = isDataUpdate 
      ? utilizationData?.Provider 
      : providerData;
    
    // Turn off this exclude logic to include all providers as base - by Hui 2025-05-6
    // return sourceData?.filter(hasAvailabilityDetails) || [];
    return sourceData || [];
  }, [providerData, utilizationData, isDataUpdate]);

  // Apply filters to clients
  const filteredClients = useMemo(() => {
    if (!validClients.length || isLoading) return [];
    return filterPlanners<ClientForms>(
      filterValues,
      searchValue,
      validClients
    );
  }, [validClients, filterValues, searchValue, isLoading]);

  // Apply filters to providers
  const filteredProviders = useMemo(() => {
    if (!validProviders.length || isLoading) return [];
    return filterPlanners<ProviderForms>(
      filterValues,
      searchValue,
      validProviders
    );
  }, [validProviders, filterValues, searchValue, isLoading]);

  // Generate filtered IDs
  const filteredIds = useMemo(() => {
    const clientIds = filteredClients.map(client => client.clientBasicDetails.id);
    const providerIds = filteredProviders.map(provider => provider.employmentDetails.id);
    return [...clientIds, ...providerIds];
  }, [filteredClients, filteredProviders]);

  // Check if we have valid data
  const hasValidData = useMemo(() => {
    return !isLoading && (filteredClients.length > 0 || filteredProviders.length > 0);
  }, [filteredClients.length, filteredProviders.length, isLoading]);

  return {
    filteredClients,
    filteredProviders,
    filteredIds,
    hasValidData
  };
};